import React, { useState } from "react"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import styled from "@emotion/styled"
import parse from "html-react-parser"
import { useForm } from "react-hook-form"
import { useDispatch } from "react-redux"
import { setCart } from "../../app/features/cartSlice"

// app components
import CartIcon from "../../icons/Cart.svg"
import Edges from "../Edges"
import Button from "../button/Button"
import Gallery from "./Gallery"
import CustomBadge from "../badges/CustomBadge"
import Breadcrumbs from "../breadcrumbs/Breadcrumbs"
import useThemeOptions from "../../hooks/useThemeOptions"
import Price from "./Price"
import { useShopify } from "../../shopify"
import PaybrightFinancing from "./PaybrightFinancing"
import VariantOptions from "./VariantOptions"
import ErpBadges from "./ErpBadges"
import isBrowser from "../../utils/isBrowser"
import AnchorLinks from "./AnchorLinks"
import TotalReviewScore from "./reviews/TotalReviewScore"
import getSelectedVariant from "../../shopify/utils/getSelectedVariant"
import Quantity from "./Quantity"
import GetNotified from "./back-in-stock-notification"

const Product = props => {
  const {
    product,
    badge,
    paybrightFinancingAvailable,
    brand,
    compatibleApps,
    shortDescription,
    buyersGuide,
  } = props

  const {
    shopifyId,
    title,
    description: content,
    variants,
    options,
  } = product || {}

  const {
    productSettings: { anchorLinks },
  } = useThemeOptions()

  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)

  const defaultValues = {}
  options.forEach(option => {
    defaultValues[option?.name] = option?.values?.[0]
  })

  const { watch, setValue } = useForm({ defaultValues })
  const formValues = watch()

  const { addLineItem, getLiveVariant } = useShopify()

  const selectedVariant = getSelectedVariant({ variants, formValues })

  const liveVariant = getLiveVariant?.({
    shopifyId,
    variantId: selectedVariant?.shopifyId,
  })

  const [quantity, setQuantity] = useState(1)

  const handleQuantityChange = value => {
    if (!value) {
      setQuantity(0)
    } else {
      setQuantity(parseInt(value))
    }
  }

  const handleAddToCart = () => {
    dispatch(setCart(true))

    setLoading(true)

    addLineItem?.({
      notification:
        quantity === 1
          ? `Added ${title} to cart`
          : `Added ${quantity} ${title} to cart`,
      variant: selectedVariant,
      quantity,
      onCompleted: () => setLoading(false),
      onError: () => setLoading(false),
    })
  }

  const brandImage =
    brand?.featuredImage && getImage(brand.featuredImage.node.gatsbyImage)

  const availableForSale =
    liveVariant?.availableForSale || selectedVariant?.availableForSale

  return (
    <>
      <div className="mx-auto w-full max-w-7xl">
        <Breadcrumbs
          className="px-[15px]"
          breadcrumbs={[
            { label: "Products", url: "/products" },
            { label: title, url: "", activeItem: true },
          ]}
        />

        <Container className="px-[15px]">
          <Gallery
            shopifyMedia={product?.media}
            thumbnailImages={product?.media}
          />

          <Details>
            {badge?.label && <CustomBadge className="mb-[20px]" {...badge} />}

            {brand?.featuredImage && (
              <GatsbyImage
                image={brandImage}
                className="mb-[18px] max-w-[200px]"
                alt={brand?.featuredImage?.altText || ""}
              />
            )}

            <h1 className="mb-2 text-h2 text-darkBlue">{title}</h1>

            {shortDescription && (
              <p className="text-body-1 text-darkGrey">{shortDescription}</p>
            )}

            {selectedVariant?.sku && (
              <span className="text-body2 text-darkGrey">
                Item # {selectedVariant.sku}
              </span>
            )}

            {product?.yotpoProductBottomLine && (
              <TotalReviewScore
                yotpoProductBottomLine={product?.yotpoProductBottomLine}
              />
            )}

            <Price
              className="mt-[13px]"
              price={liveVariant?.price?.amount || selectedVariant?.price}
              compareAtPrice={
                liveVariant?.compareAtPrice?.amount ||
                selectedVariant?.compareAtPrice
              }
            />

            {paybrightFinancingAvailable && <PaybrightFinancing />}

            <div className="flex flex-wrap items-center">
              <VariantOptions
                variants={variants}
                options={options}
                formValues={formValues}
                setValue={setValue}
              />

              <Quantity value={quantity} onChange={handleQuantityChange} />
            </div>

            <ErpBadges product={product} availableForSale={availableForSale} />

            <div className="flex flex-col">
              {availableForSale && (
                <Button
                  className="mt-[15px] mb-[23px] flex max-h-[40px] max-w-[325px] items-center justify-center py-[25px] lg:max-w-[417px]"
                  fullWidth
                  loading={loading}
                  onClick={() => handleAddToCart(isBrowser ? window : false)}
                  disabled={!availableForSale}
                >
                  {availableForSale ? (
                    <div className="flex items-center">
                      <CartIcon className="mr-3 mb-1 w-[16px]" />
                      Add to cart
                    </div>
                  ) : (
                    `Sold out`
                  )}
                </Button>
              )}

              {!availableForSale && (
                <GetNotified
                  product={product}
                  selectedVariant={selectedVariant}
                />
              )}

              <AnchorLinks
                selectedVariant={selectedVariant}
                buyersGuide={buyersGuide}
                compatibleApps={compatibleApps}
                links={anchorLinks}
              />
              <p className="font-darkGrey mt-[17px] text-[14px] font-normal">
                Shipping Calculated at checkout
              </p>
            </div>
          </Details>
        </Container>
      </div>

      {content && (
        <div className="mb-[15px] bg-lightGrey py-[40px] px-[15px] xl:px-0">
          <Edges size="md">
            <h4 className="mb-1 text-darkBlue ">Description</h4>

            <div className="text-darkGrey">{content && parse(content)}</div>
          </Edges>
        </div>
      )}
    </>
  )
}

export default Product

const Container = styled.div`
  margin: 30px 0;
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap-reverse;
  justify-content: space-between;
  > div {
    margin-bottom: 30px;
    width: 100%;
    @media (min-width: 768px) {
      width: calc(50% - 20px);
    }
  }
`

const Details = styled.div`
  @media (min-width: 768px) {
    position: sticky;
    top: 90px;
  }
`
